import axios from 'axios';

// Use relative URL if REACT_APP_BACKEND_URL is not set or empty
// This allows the app to work on any domain (preview or production)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Enable sending cookies with cross-origin requests
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 from /auth/me - this is expected for anonymous users
    // Only redirect when accessing protected resources (not auth check endpoints)
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isAuthCheck = requestUrl.includes('/auth/me') || requestUrl.includes('/auth/profile');
      
      // Only redirect if:
      // 1. Not an auth check endpoint (those are expected to fail for anonymous users)
      // 2. Not already on a login page
      // 3. User had a token (was logged in but token expired)
      const hadToken = localStorage.getItem('token');
      
      if (!isAuthCheck && !window.location.pathname.includes('login') && hadToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/admin-login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  getProfile: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
};

// Projects APIs
export const projectsAPI = {
  list: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Zoning APIs
export const zoningAPI = {
  get: (projectId) => api.get(`/zoning/${projectId}`),
  create: (data) => api.post('/zoning', data),
  extractPDF: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/zoning/extract-pdf/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Analysis APIs
export const analysisAPI = {
  run: (projectId) => api.post(`/analysis/${projectId}`),
  getReport: (projectId) => api.get(`/analysis/report/${projectId}`, {
    responseType: 'blob',
  }),
};

// Admin APIs
export const adminAPI = {
  listArrondissements: () => api.get('/admin/arrondissements'),
  createArrondissement: (data) => api.post('/admin/arrondissements', data),
  updateArrondissement: (id, data) => api.put(`/admin/arrondissements/${id}`, data),
  deleteArrondissement: (id) => api.delete(`/admin/arrondissements/${id}`),
  listZoneTemplates: (arrondissementId) => 
    api.get('/admin/zone-templates', { params: { arrondissement_id: arrondissementId } }),
  createZoneTemplate: (data) => api.post('/admin/zone-templates', data),
  updateZoneTemplate: (id, data) => api.put(`/admin/zone-templates/${id}`, data),
  deleteZoneTemplate: (id) => api.delete(`/admin/zone-templates/${id}`),
  listUsers: () => api.get('/admin/users'),
  updateUserRole: (id, role) => api.put(`/admin/users/${id}/role`, null, { params: { role } }),
  resetPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  listOrganisations: () => api.get('/admin/organisations'),
  createOrganisation: (data) => api.post('/admin/organisations', data),
  updateOrganisationSeats: (id, seats) => api.patch(`/admin/organisations/${id}`, { seats }),
  addMember: (organisationId, data) => api.post(`/admin/organisations/${organisationId}/members`, data),
  recordPayment: (organisationId, receivedOn) =>
    api.put(`/admin/organisations/${organisationId}/payment`, { received_on: receivedOn }),
  undoPayment: (organisationId) => api.delete(`/admin/organisations/${organisationId}/payment`),
  changeTrialEnd: (organisationId, endsOn) =>
    api.put(`/admin/organisations/${organisationId}/trial-end`, { ends_on: endsOn }),
  listSecteurExports: () => api.get('/admin/export/secteurs'),
  exportSecteursCsv: (territoire) =>
    api.get('/admin/export/secteurs.csv', { params: { territoire }, responseType: 'blob' }),
};

// The member's own subscription (docs/specs/subscription-page)
export const subscriptionAPI = {
  own: () => api.get('/subscription'),
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Address Lookup API
export const addressAPI = {
  lookup: (address) => api.post('/address-lookup', null, { params: { address } }),
};

// Lot Lookup API (NEW - primary flow)
export const lotAPI = {
  lookup: (lotNumber) => api.post('/lot-lookup', null, { params: { lot_number: lotNumber } }),
};

// Regulations API (secteurs de zonage)
export const regulationsAPI = {
  getBySector: (sectorNumber) => api.get(`/regulations/sector/${sectorNumber}`),
};

// Sainte-Thérèse API (grilles des usages et normes, Règlement 1200 N.S.)
export const steThereseAPI = {
  listZones: () => api.get('/regulations/ste-therese'),
  getZone: (zoneCode) => api.get(`/regulations/ste-therese/${encodeURIComponent(zoneCode)}`),
};

// Arrondissements API
export const arrondissementsAPI = {
  list: () => api.get('/arrondissements'),
  get: (name) => api.get(`/arrondissements/${encodeURIComponent(name)}`),
};

// Address Autocomplete API
export const addressAutocompleteAPI = {
  suggest: (query, limit = 5) =>
    api.get('/address/suggest', { params: { q: query, limit } }),
  validate: (magicKey) =>
    api.post('/address/validate', null, { params: { magic_key: magicKey } }),
  geocode: (address) =>
    api.post('/address/geocode', null, { params: { address } }),
};

// Regulatory Context API
export const regulatoryAPI = {
  getContext: (lat, lng, arrondissement = '') =>
    api.get('/regulatory/context', { params: { lat, lng, arrondissement } }),
};

export default api;
